(() => {
  if (window.__pelicanworkProductSwapLoaded) return;
  window.__pelicanworkProductSwapLoaded = true;

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log("[PelicanWork Product Swap]", ...args);

  const MAIN_SECTION_SELECTOR = ".shopify-section--main-product";
  const SWAP_SCOPE_SELECTOR = ".pw-connected-product-picker";
  const SWAP_LINK_SELECTOR = `${SWAP_SCOPE_SELECTOR} a[href]`;

  const CACHE_TTL_MS = 1000 * 60 * 5;
  const MAX_SECTION_CACHE_ITEMS = 10;

  const FULL_RELOAD_SCROLL_KEY = "pw-product-full-reload-scroll-restore";
  const FULL_RELOAD_SCROLL_TTL_MS = 1000 * 12;

  const sectionCache = new Map();
  const parser = new DOMParser();

  let activeSwapController = null;
  let activeSwapToken = 0;
  let fitTitleFrame = null;

  const isPlainLeftClick = (event) => {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  };

  const getMainSection = () => document.querySelector(MAIN_SECTION_SELECTOR);

  const getSectionId = (section) => {
    if (!section || !section.id) return "";
    return section.id.replace(/^shopify-section-/, "");
  };

  const normalizeProductUrl = (href) => {
    try {
      const url = new URL(href, window.location.origin);

      if (url.origin !== window.location.origin) return null;
      if (!url.pathname.includes("/products/")) return null;

      return url;
    } catch (error) {
      return null;
    }
  };

  const comparableUrl = (url) => {
    const clone = new URL(url.href);
    clone.hash = "";
    clone.searchParams.delete("section_id");
    return `${clone.pathname}${clone.search}`;
  };

  const cleanPublicUrl = (url) => {
    const clone = new URL(url.href);
    clone.hash = "";
    clone.searchParams.delete("section_id");
    return clone;
  };

  const pruneMap = (map, maxItems) => {
    while (map.size > maxItems) {
      map.delete(map.keys().next().value);
    }
  };

  const pruneExpiredSectionCache = () => {
    const now = Date.now();

    for (const [key, cached] of sectionCache) {
      if (!cached || now - cached.createdAt > CACHE_TTL_MS) {
        sectionCache.delete(key);
      }
    }

    pruneMap(sectionCache, MAX_SECTION_CACHE_ITEMS);
  };

  // Native variant options (e.g. Size) carry their own product-scoped variant
  // IDs, so swapping to a different connected product can't match on value.
  // We match on the option's visible label text instead, which is stable
  // across the connected products.
  const getSelectedOptionLabels = (section) => {
    const picker = section?.querySelector?.('variant-picker[form-id]');
    if (!picker) return [];

    return Array.from(picker.querySelectorAll('input[data-option-position]:checked'))
      .map((input) => picker.querySelector(`label[for="${input.id}"]`)?.textContent?.trim())
      .filter(Boolean);
  };

  // Same native options as getSelectedOptionLabels, but as raw option VALUES
  // (not label text) — this is what the section-rendering endpoint's
  // ?option_values= param expects (see theme.js's own VariantPicker,
  // renderForCombination_fn), so we can ask the server to pre-select the
  // preserved combination in the SAME fetch that swaps the connected
  // product, instead of fetching the target product's default variant and
  // then re-selecting client-side in a second, visibly laggy round trip.
  const getSelectedOptionValues = (section) => {
    const picker = section?.querySelector?.('variant-picker[form-id]');
    if (!picker) return [];

    return Array.from(picker.querySelectorAll('input[data-option-position]:checked'))
      .map((input) => input.value)
      .filter(Boolean);
  };

  const applyPreservedOptionLabels = (section, labels) => {
    const picker = section?.querySelector?.('variant-picker[form-id]');
    if (!picker || !labels?.length) return;

    labels.forEach((label) => {
      const match = Array.from(picker.querySelectorAll('input[data-option-position]'))
        .map((input) => picker.querySelector(`label[for="${input.id}"]`))
        .find((candidate) => candidate?.textContent?.trim() === label);

      if (match && !match.control?.checked) match.click();
    });
  };

  // Reads the id of whichever variant the swapped-in section actually
  // rendered (relevant once ?option_values= is used to pre-select a
  // preserved native option server-side — see fetchMainProductSection). We
  // fold that into the URL we push so refreshing/sharing the link doesn't
  // lose the preserved selection; previously this only happened as a
  // side-effect of the client-side re-click in applyPreservedOptionLabels,
  // which option_values now makes unnecessary in the common case.
  const getSectionVariantId = (section) => {
    try {
      const raw = section?.querySelector?.('variant-picker[form-id] script[data-variant]')?.textContent;
      const data = JSON.parse(raw || "{}");
      return data?.id ? String(data.id) : "";
    } catch (error) {
      return "";
    }
  };

  // Shopify's section-rendering endpoint (?section_id=) can include an extra
  // hidden native-select fallback for the "id" field alongside the normal
  // hidden input — a full page load never renders both. Two elements sharing
  // name="id" makes `form.id` resolve to a RadioNodeList instead of a single
  // element, which crashes the theme's variant-picker (it calls
  // .dispatchEvent() on it) and silently aborts the rest of the variant
  // update, including the media/image swap. Collapse back to one element so
  // the swapped-in section behaves like a normal page load.
  const dedupeDuplicateFormIdFields = (section) => {
    const idFields = Array.from(section?.querySelectorAll?.('[name="id"]') || []);
    if (idFields.length <= 1) return;

    const primary = idFields.find((field) => field.tagName === "INPUT") || idFields[0];
    primary.disabled = false;

    idFields.forEach((field) => {
      if (field !== primary) field.removeAttribute("name");
    });
  };

  const shouldPrefetch = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (!connection) return true;
    if (connection.saveData) return false;
    if (connection.effectiveType && /(^|-)2g$/i.test(connection.effectiveType)) return false;

    return true;
  };

  const dispatchThemeLoading = (state) => {
    document.documentElement.dispatchEvent(
      new CustomEvent(`theme:loading:${state}`, { bubbles: true })
    );
  };

  const dispatchSectionLoad = (section, sectionId) => {
    if (!section) return;

    section.dispatchEvent(
      new CustomEvent("shopify:section:load", {
        bubbles: true,
        detail: {
          sectionId,
          section
        }
      })
    );
  };

  const getStickyOffset = () => {
    const rootHeaderHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;

    const visibleHeaderHeight =
      document.querySelector("x-header, .shopify-section--header, header")
        ?.getBoundingClientRect?.()
        ?.height || 0;

    return Math.max(0, rootHeaderHeight, visibleHeaderHeight);
  };

  const saveFullReloadScrollRestore = (targetUrl, clickedLink = null) => {
    try {
      const restoreTarget =
        clickedLink?.closest?.(SWAP_SCOPE_SELECTOR) ||
        document.querySelector(SWAP_SCOPE_SELECTOR) ||
        document.querySelector(".product-info") ||
        getMainSection();

      const viewportTop = restoreTarget
        ? restoreTarget.getBoundingClientRect().top
        : getStickyOffset() + 12;

      sessionStorage.setItem(
        FULL_RELOAD_SCROLL_KEY,
        JSON.stringify({
          targetPath: targetUrl.pathname,
          selector: SWAP_SCOPE_SELECTOR,
          viewportTop,
          scrollY: window.scrollY,
          createdAt: Date.now()
        })
      );
    } catch (error) {
      // Ignore private-mode/sessionStorage failures.
    }
  };

  const restoreFullReloadScrollPosition = () => {
    let payload = null;

    try {
      payload = JSON.parse(sessionStorage.getItem(FULL_RELOAD_SCROLL_KEY) || "null");
    } catch (error) {
      payload = null;
    }

    if (!payload) return;

    const isExpired = Date.now() - Number(payload.createdAt || 0) > FULL_RELOAD_SCROLL_TTL_MS;
    const isSamePath = payload.targetPath === window.location.pathname;

    if (isExpired || !isSamePath) {
      try {
        sessionStorage.removeItem(FULL_RELOAD_SCROLL_KEY);
      } catch (error) {}

      return;
    }

    try {
      sessionStorage.removeItem(FULL_RELOAD_SCROLL_KEY);
    } catch (error) {}

    const restore = () => {
      const target =
        document.querySelector(payload.selector) ||
        document.querySelector(".product-info") ||
        getMainSection();

      if (target) {
        const currentTargetTop = target.getBoundingClientRect().top + window.scrollY;
        const savedViewportTop = Number(payload.viewportTop);
        const desiredViewportTop = Number.isFinite(savedViewportTop)
          ? savedViewportTop
          : getStickyOffset() + 12;

        const desiredScrollTop = Math.max(0, currentTargetTop - desiredViewportTop);

        window.scrollTo({ top: desiredScrollTop, left: 0, behavior: "auto" });
      } else if (Number.isFinite(Number(payload.scrollY))) {
        window.scrollTo({ top: Number(payload.scrollY), left: 0, behavior: "auto" });
      }
    };

    requestAnimationFrame(restore);
    setTimeout(restore, 120);
    setTimeout(restore, 450);
    setTimeout(restore, 900);
  };

  // Prefetching only warms the section's HTML text — the browser never sees
  // an <img> tag, so it never downloads the actual picture. Without this, a
  // swap that outruns the network (common on mobile, where there's no hover
  // to prefetch on) briefly shows an empty/broken gallery, including the
  // images further down the scroll that aren't the featured shot. Force the
  // first few gallery images to start downloading as soon as we have the
  // HTML, not when they're inserted into the DOM. Mobile and desktop use the
  // same markup but different breakpoint-driven srcset/sizes (see the
  // `sizes` capture in product-gallery.liquid), so we copy those attributes
  // rather than just `src`, letting the browser pick the size it would
  // actually use on this viewport instead of always fetching one fixed size.
  //
  // We measured mobile as ~7x cheaper per image before checking against a
  // real device — that number came from a 1x-pixel-density viewport. A real
  // phone's retina display (e.g. iPhone 16, 3x) requests a wider file to
  // compensate for the smaller CSS viewport, and ends up costing about the
  // *same or slightly more* per image than desktop (measured: 1773KB vs
  // 1521KB for the same photo). So there's no mobile size discount to spend
  // on a higher limit — keep both the same, conservative amount.
  const GALLERY_PRELOAD_LIMIT = 4;

  const preloadImageElement = (img) => {
    const src = img.getAttribute("src");
    const srcset = img.getAttribute("srcset");
    if (!src && !srcset) return;

    const image = new Image();
    if (img.getAttribute("sizes")) image.sizes = img.getAttribute("sizes");
    if (srcset) image.srcset = srcset;
    image.src = src ? (src.startsWith("//") ? `https:${src}` : src) : "";
  };

  // "full" preloads the first few large gallery images; "hero" preloads just
  // the single featured one. Every connected-product option (Color, Legs,
  // etc.) triggers its own speculative fetch when its picker row comes into
  // view (see prefetchConnectedProductImages), so "full" for all of them at
  // once would be tens of MB before the user has clicked anything. Most of
  // that is wasted on options they never pick; "hero" for speculative
  // prefetches still means whichever one they do click already has its main
  // photo ready, without paying for the rest of every gallery up front.
  //
  // The thumbnail strip is preloaded at both levels regardless: mobile shows
  // only the one featured image by default and taps a thumbnail to jump to
  // another rather than scrolling, so a sibling color's thumbnail row needs
  // to be tappable-and-populated as soon as that color is swapped to, not
  // just its hero shot. It's cheap enough (~42KB each) that this doesn't
  // meaningfully add to the "hero" tier's bandwidth savings.
  const preloadGalleryImages = (section, level = "full") => {
    const featured = section?.querySelector?.(".product-gallery__media.is-initial img");

    if (level === "hero") {
      if (featured) preloadImageElement(featured);
    } else {
      const all = Array.from(section?.querySelectorAll?.(".product-gallery__media img") || []);
      const ordered = featured ? [featured, ...all.filter((img) => img !== featured)] : all;

      ordered.slice(0, GALLERY_PRELOAD_LIMIT).forEach(preloadImageElement);
    }

    // The mobile thumbnail strip (.product-gallery__thumbnail-list, shown
    // only when mobile_controls is "thumbnails" — hidden on desktop, so it's
    // easy to miss testing only on desktop) is visible immediately, not
    // scrolled to.
    Array.from(section?.querySelectorAll?.(".product-gallery__thumbnail img") || []).forEach(
      preloadImageElement
    );
  };

  const getSectionFromHtml = (html, sectionId, currentSection) => {
    const doc = parser.parseFromString(html, "text/html");

    let nextSection =
      doc.getElementById(`shopify-section-${sectionId}`) ||
      doc.querySelector(MAIN_SECTION_SELECTOR);

    if (nextSection) {
      return {
        section: nextSection,
        title: doc.querySelector("title")?.textContent?.trim() || ""
      };
    }

    const temp = document.createElement("div");
    temp.innerHTML = html;

    nextSection =
      temp.querySelector(`#shopify-section-${sectionId}`) ||
      temp.querySelector(MAIN_SECTION_SELECTOR);

    if (nextSection) {
      return { section: nextSection, title: "" };
    }

    if (currentSection && temp.querySelector(".product") && temp.querySelector(".product-info")) {
      const wrapper = currentSection.cloneNode(false);
      wrapper.innerHTML = html;
      return { section: wrapper, title: "" };
    }

    return null;
  };

  const requestText = async (url, signal) => {
    const response = await fetch(url.href, {
      credentials: "same-origin",
      signal,
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    if (!response.ok) {
      throw new Error(`Product swap fetch failed: ${response.status}`);
    }

    return response.text();
  };

  const fetchMainProductSection = async (targetUrl, currentSection, options = {}) => {
    const sectionId = getSectionId(currentSection);
    const { signal, imagePreload = "full", optionValues = [] } = options;

    if (!sectionId) {
      throw new Error("Missing main product section id.");
    }

    pruneExpiredSectionCache();

    const optionValuesKey = optionValues.length ? optionValues.join(",") : "";
    const cacheKey = `${sectionId}::${comparableUrl(targetUrl)}::${optionValuesKey}`;
    const cached = sectionCache.get(cacheKey);

    if (cached && Date.now() - cached.createdAt < CACHE_TTL_MS) {
      sectionCache.delete(cacheKey);
      sectionCache.set(cacheKey, cached);

      const cachedSection = cached.section.cloneNode(true);
      preloadGalleryImages(cachedSection, imagePreload);

      return {
        section: cachedSection,
        title: cached.title,
        url: new URL(cached.url)
      };
    }

    const sectionUrl = new URL(targetUrl.href);
    sectionUrl.hash = "";
    sectionUrl.searchParams.set("section_id", sectionId);
    if (optionValuesKey) sectionUrl.searchParams.set("option_values", optionValuesKey);

    let result = null;

    try {
      const sectionHtml = await requestText(sectionUrl, signal);
      result = getSectionFromHtml(sectionHtml, sectionId, currentSection);
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      result = null;
    }

    if (!result || !result.section?.querySelector(".product") || !result.section?.querySelector(".product-info")) {
      const pageUrl = new URL(targetUrl.href);
      if (optionValuesKey) pageUrl.searchParams.set("option_values", optionValuesKey);

      const pageHtml = await requestText(pageUrl, signal);
      result = getSectionFromHtml(pageHtml, sectionId, currentSection);
    }

    if (!result || !result.section?.querySelector(".product") || !result.section?.querySelector(".product-info")) {
      throw new Error("Could not find rendered main product section in target product page.");
    }

    const finalResult = {
      section: result.section,
      title: result.title || "",
      url: targetUrl.href
    };

    preloadGalleryImages(finalResult.section, imagePreload);

    sectionCache.set(cacheKey, {
      section: finalResult.section.cloneNode(true),
      title: finalResult.title,
      url: finalResult.url,
      createdAt: Date.now()
    });

    pruneMap(sectionCache, MAX_SECTION_CACHE_ITEMS);

    return finalResult;
  };

  const getProductIdFromSection = (section) => {
    const rerenderId = section
      ?.querySelector?.('product-rerender[id^="product-info-"]')
      ?.id
      ?.match(/^product-info-(\d+)-/)?.[1];

    if (rerenderId) return String(rerenderId);

    return (
      section?.querySelector?.("[data-yotpo-product-id]")?.getAttribute("data-yotpo-product-id") ||
      section?.querySelector?.("[data-product-id]")?.getAttribute("data-product-id") ||
      ""
    );
  };

  const refreshThirdPartyWidgets = () => {
    window.Shopify?.PaymentButton?.init?.();

    window.yotpo?.initWidgets?.();
    window.yotpo?.refreshWidgets?.();
    window.yotpoWidgetsContainer?.initWidgets?.();

    window.Judgeme?.init?.();
    window.jdgm?.initializeBadges?.();

    window.SPR?.registerCallbacks?.();
    window.SPR?.initDomEls?.();
    window.SPR?.loadProducts?.();
    window.SPR?.loadBadges?.();
  };

  const updateRecentlyViewed = (section) => {
    const productId = getProductIdFromSection(section);
    if (!productId) return;

    try {
      let recentlyViewedProducts = JSON.parse(
        localStorage.getItem("theme:recently-viewed-products") || "[]"
      );

      recentlyViewedProducts = recentlyViewedProducts.filter((item) => String(item) !== String(productId));
      recentlyViewedProducts.unshift(Number(productId));

      localStorage.setItem("theme:recently-viewed-products", JSON.stringify(recentlyViewedProducts));
    } catch (error) {
      // Ignore private-mode/localStorage failures.
    }
  };

  const fitProductTitle = (section) => {
    const title = section?.querySelector?.(".pw-product-title-group .product-title");
    if (!title || !title.clientWidth) return;

    const getStartSize = () => {
      if (window.matchMedia("(max-width: 699px)").matches) return 22;
      if (window.matchMedia("(max-width: 989px)").matches) return 34;
      return 46;
    };

    const getMinimumSize = () => {
      if (window.matchMedia("(max-width: 699px)").matches) return 15.5;
      if (window.matchMedia("(max-width: 989px)").matches) return 22;
      return 28;
    };

    title.style.removeProperty("--pw-pdp-title-fit-size");

    let size = getStartSize();
    const minimumSize = getMinimumSize();
    let guard = 0;

    title.style.setProperty("--pw-pdp-title-fit-size", `${size}px`);

    while (title.scrollWidth > title.clientWidth && size > minimumSize && guard < 80) {
      size -= 0.5;
      title.style.setProperty("--pw-pdp-title-fit-size", `${size}px`);
      guard += 1;
    }
  };

  const scheduleFitProductTitle = (section = getMainSection()) => {
    if (!section) return;

    if (fitTitleFrame) {
      cancelAnimationFrame(fitTitleFrame);
    }

    fitTitleFrame = requestAnimationFrame(() => {
      fitTitleFrame = null;
      fitProductTitle(section);
    });
  };

  // Gallery videos only carry preload="metadata"/autoplay when they're the
  // slide actually shown first (see product-gallery.liquid) - any other
  // video in the gallery starts at preload="none" so it never downloads for
  // visitors who never reach it. This bumps just the video 1 slide away from
  // wherever the visitor currently is, so it's warm by the time they arrive
  // (this CDN/browser combo fetches the full file even at preload="metadata",
  // so this doesn't save bytes for that one adjacent slide - what it saves is
  // downloading every video in the gallery regardless of how far a visitor
  // actually scrolls).
  const GALLERY_VIDEO_QUEUE_DISTANCE = 1;
  const queuedGalleryVideos = new WeakSet();

  const queueAdjacentGalleryVideos = (carousel) => {
    const cells = carousel?.cells;
    if (!cells || !cells.length || typeof carousel.selectedIndex !== "number") return;

    const index = carousel.selectedIndex;

    for (let offset = -GALLERY_VIDEO_QUEUE_DISTANCE; offset <= GALLERY_VIDEO_QUEUE_DISTANCE; offset++) {
      if (offset === 0) continue;

      const cell = cells[index + offset];
      if (!cell || cell.dataset?.mediaType !== "video") continue;

      const video = cell.querySelector("video");
      if (!video || queuedGalleryVideos.has(video) || video.preload === "auto") continue;

      queuedGalleryVideos.add(video);
      video.setAttribute("preload", "metadata");
      video.load();
    }
  };

  const observeGalleryVideoQueue = (section) => {
    const carousels = Array.from(section?.querySelectorAll?.(".product-gallery scroll-carousel") || []);

    carousels.forEach((carousel) => {
      queueAdjacentGalleryVideos(carousel);
      carousel.addEventListener("carousel:settle", () => queueAdjacentGalleryVideos(carousel));
    });
  };

  const afterSectionSwap = (section, sectionId, targetUrl, title) => {
    updateRecentlyViewed(section);

    if (title) {
      document.title = title;
    } else {
      const productTitle = section
        .querySelector(".pw-product-title-group .product-title, .product-title")
        ?.textContent?.trim();

      if (productTitle) document.title = productTitle;
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = cleanPublicUrl(targetUrl).href;

    scheduleFitProductTitle(section);

    dispatchSectionLoad(section, sectionId);
    prefetchConnectedProductImages(section);
    observeGalleryVideoQueue(section);

    document.dispatchEvent(
      new CustomEvent("pw:product:swapped", {
        bubbles: true,
        detail: { url: targetUrl.href, section }
      })
    );
  };

  const goToProductWithFullReload = (targetUrl, clickedLink = null) => {
    saveFullReloadScrollRestore(targetUrl, clickedLink);
    window.location.href = targetUrl.href;
  };

  // The dim/blur/overlay swap indicator (.is-pw-product-swapping, see
  // main-product.liquid) previously cleared the instant the new section was
  // inserted, which is exactly when its images might still be mid-download
  // if prefetching didn't fully keep up — revealing a blank/broken photo
  // instead of the loading treatment. Wait for the featured image to
  // actually be ready (already true almost always now, given prefetching)
  // before lifting the blur, with a timeout so a slow/failed image can't
  // leave the section stuck dimmed.
  const waitForImageReady = (img, timeoutMs = 1200) => {
    if (!img) return Promise.resolve();
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      img.addEventListener("load", finish, { once: true });
      img.addEventListener("error", finish, { once: true });
      setTimeout(finish, timeoutMs);
    });
  };

  const swapToProduct = async (targetUrl, { push = true, clickedLink = null } = {}) => {
    const currentSection = getMainSection();

    if (!currentSection) {
      goToProductWithFullReload(targetUrl, clickedLink);
      return;
    }

    const preservedOptionLabels = getSelectedOptionLabels(currentSection);
    const preservedOptionValues = getSelectedOptionValues(currentSection);
    const sectionId = getSectionId(currentSection);
    const scrollY = window.scrollY;
    const swapToken = ++activeSwapToken;

    activeSwapController?.abort?.();
    activeSwapController = new AbortController();

    currentSection.classList.add("is-pw-product-swapping");
    document.documentElement.classList.add("pw-product-swap-active");
    dispatchThemeLoading("start");

    try {
      const { section: nextSection, title } = await fetchMainProductSection(targetUrl, currentSection, {
        signal: activeSwapController.signal,
        optionValues: preservedOptionValues
      });

      if (swapToken !== activeSwapToken) return;

      dedupeDuplicateFormIdFields(nextSection);
      currentSection.replaceWith(nextSection);
      // Safety net: option_values above should already have pre-selected the
      // preserved combination server-side, so this is normally a no-op
      // (labels already match, nothing to click). It only does real work if
      // that combination wasn't resolvable for the target product.
      applyPreservedOptionLabels(nextSection, preservedOptionLabels);

      const pushUrl = new URL(targetUrl.href);
      if (preservedOptionValues.length) {
        const resolvedVariantId = getSectionVariantId(nextSection);
        if (resolvedVariantId) pushUrl.searchParams.set("variant", resolvedVariantId);
      }

      if (push) {
        history.pushState({ pwProductSwap: true, url: pushUrl.href }, "", pushUrl.href);
      }

      window.scrollTo({ top: scrollY, left: 0, behavior: "auto" });

      afterSectionSwap(nextSection, sectionId, targetUrl, title);

      const featuredImage =
        nextSection.querySelector(".product-gallery__media.is-initial img") ||
        nextSection.querySelector(".product-gallery__media img");
      await waitForImageReady(featuredImage);
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.warn("[PelicanWork] Product swap failed; falling back to normal navigation.", error);
      goToProductWithFullReload(targetUrl, clickedLink);
    } finally {
      if (swapToken === activeSwapToken) {
        document.documentElement.classList.remove("pw-product-swap-active");
        dispatchThemeLoading("end");

        const latestSection = getMainSection();
        latestSection?.classList.remove("is-pw-product-swapping");
      }
    }
  };

  const prefetchProduct = (targetUrl, imagePreload = "full") => {
    const currentSection = getMainSection();

    if (!shouldPrefetch()) return;
    if (!currentSection) return;
    if (comparableUrl(targetUrl) === comparableUrl(new URL(window.location.href))) return;

    // Warm the cache for the combination the click will actually request
    // (current native option selections carried over), not just the target
    // product's default variant — otherwise the eventual click still pays
    // for a second, uncached ?option_values= fetch.
    const optionValues = getSelectedOptionValues(currentSection);

    fetchMainProductSection(targetUrl, currentSection, { imagePreload, optionValues }).catch(() => {});
  };

  // Any connected-product option (Color, Legs, or whatever else a product
  // uses this picker for) is a likely next click — someone comparing options
  // tends to click through several in a row. Warm the cache for every
  // sibling across every connected-product picker on the page (not just
  // Color) while it's in view (and idle), so those follow-up clicks land
  // instantly. Mirrors the same intersection + requestIdleCallback pattern
  // the native variant-picker (theme.js) already uses for its own options.
  //
  // On mobile, only the hero image + thumbnails are preloaded per sibling,
  // not the full gallery — with several colors and legs styles combined,
  // "full" for every one of them on a picker-row view would be tens of MB
  // before anything is even clicked, which is a worse mobile-data cost than
  // the blank-gallery glitch this is meant to prevent. Desktop keeps the
  // full preload per sibling since it's typically on a less metered
  // connection.
  const prefetchConnectedProductImages = (section) => {
    if (!shouldPrefetch()) return;

    const scopes = Array.from(section?.querySelectorAll?.(SWAP_SCOPE_SELECTOR) || []);
    if (!scopes.length) return;

    const run = () => {
      const imagePreload = window.matchMedia("(max-width: 699px)").matches ? "hero" : "full";

      scopes.forEach((scope) => {
        Array.from(scope.querySelectorAll("a[href]")).forEach((link) => {
          const targetUrl = normalizeProductUrl(link.getAttribute("href"));
          if (targetUrl) prefetchProduct(targetUrl, imagePreload);
        });
      });
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      run();
    }
  };

  const observeConnectedProductScopesForPrefetch = (section) => {
    const scopes = Array.from(section?.querySelectorAll?.(SWAP_SCOPE_SELECTOR) || []);
    if (!scopes.length) return;

    let fired = false;
    const observer = new IntersectionObserver((entries) => {
      if (fired || !entries.some((entry) => entry.isIntersecting)) return;
      fired = true;
      observer.disconnect();
      prefetchConnectedProductImages(section);
    });

    scopes.forEach((scope) => observer.observe(scope));
  };

  document.addEventListener("click", (event) => {
    const link = event.target.closest?.(SWAP_LINK_SELECTOR);

    if (!link || event.defaultPrevented || !isPlainLeftClick(event)) return;

    const targetUrl = normalizeProductUrl(link.getAttribute("href"));
    if (!targetUrl) return;

    if (comparableUrl(targetUrl) === comparableUrl(new URL(window.location.href))) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    swapToProduct(targetUrl, { push: true, clickedLink: link });
  });

  document.addEventListener(
    "pointerenter",
    (event) => {
      const link = event.target.closest?.(SWAP_LINK_SELECTOR);
      const targetUrl = link ? normalizeProductUrl(link.getAttribute("href")) : null;

      if (targetUrl) prefetchProduct(targetUrl);
    },
    true
  );

  document.addEventListener(
    "touchstart",
    (event) => {
      const link = event.target.closest?.(SWAP_LINK_SELECTOR);
      const targetUrl = link ? normalizeProductUrl(link.getAttribute("href")) : null;

      if (targetUrl) prefetchProduct(targetUrl);
    },
    { passive: true }
  );

  document.addEventListener("focusin", (event) => {
    const link = event.target.closest?.(SWAP_LINK_SELECTOR);
    const targetUrl = link ? normalizeProductUrl(link.getAttribute("href")) : null;

    if (targetUrl) prefetchProduct(targetUrl);
  });

  window.addEventListener("popstate", () => {
    const targetUrl = normalizeProductUrl(window.location.href);

    if (!targetUrl) {
      window.location.reload();
      return;
    }

    swapToProduct(targetUrl, { push: false });
  });

  const initialSection = getMainSection();

  if (initialSection) {
    history.replaceState({ pwProductSwap: true, url: window.location.href }, "", window.location.href);
    observeConnectedProductScopesForPrefetch(initialSection);
    observeGalleryVideoQueue(initialSection);
  }

  window.addEventListener("resize", () => {
    scheduleFitProductTitle(getMainSection());
  });

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      scheduleFitProductTitle(getMainSection());
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    const loadedSection =
      event.detail?.section ||
      (event.target?.matches?.(MAIN_SECTION_SELECTOR) ? event.target : null) ||
      event.target?.closest?.(MAIN_SECTION_SELECTOR) ||
      getMainSection();

    if (!loadedSection?.matches?.(MAIN_SECTION_SELECTOR)) return;

    window.requestAnimationFrame(() => {
      refreshThirdPartyWidgets();
      scheduleFitProductTitle(loadedSection);
    });
  });

  restoreFullReloadScrollPosition();
  scheduleFitProductTitle(initialSection);
})();